import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { initFcmForUser, onForegroundMessage } from '../services/fcmService';

// Banner that asks users to allow notifications, shown once per browser.
export default function NotificationPrompt() {
  const { user, isAuthenticated } = useAuth();
  const [show, setShow] = useState(false);
  const [toast, setToast] = useState(null);

  // Decide whether to show the prompt
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    if (sessionStorage.getItem('notif_prompt_dismissed')) return;
    setShow(true);
  }, [isAuthenticated]);

  // Listen for foreground messages and show an in-app toast
  useEffect(() => {
    if (!isAuthenticated) return;
    let unsub;
    onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'Vivifyer';
      const body = payload.notification?.body || '';
      setToast({ title, body });
      setTimeout(() => setToast(null), 5000);
    }).then((fn) => { unsub = fn; });
    return () => { if (unsub) unsub(); };
  }, [isAuthenticated]);

  const handleAllow = async () => {
    setShow(false);
    if (user?.id) await initFcmForUser(user.id);
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('notif_prompt_dismissed', '1');
  };

  return (
    <>
      {/* Permission banner */}
      {show && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm
                        bg-white border border-emerald-200 rounded-2xl shadow-xl px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <span className="text-lg">🔔</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-slate-800">Stay on track</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Get daily workout reminders and streak alerts.
              </p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAllow}
              className="flex-1 py-2 rounded-xl text-sm font-bold text-white
                         bg-gradient-to-r from-emerald-600 to-teal-600"
            >
              Allow
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 py-2 rounded-xl text-sm font-semibold
                         text-slate-600 bg-slate-100"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Foreground message toast */}
      {toast && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm
                     bg-slate-900 text-white rounded-2xl shadow-xl px-4 py-3 flex items-start gap-3"
          onClick={() => setToast(null)}
        >
          <span className="text-lg shrink-0">💪</span>
          <div className="min-w-0">
            <p className="font-bold text-sm">{toast.title}</p>
            {toast.body && <p className="text-xs text-slate-300 mt-0.5">{toast.body}</p>}
          </div>
        </div>
      )}
    </>
  );
}
